import { Component, OnInit, OnDestroy, Renderer2, HostListener } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseService } from '../../services/base.service';
import {
  LOGIN,
  RECAPTCHA_TOKEN,
  RESEND_VERIFY,
} from 'src/app/constants/endpoint.const';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { LocalStorageService } from '../../services/local-storage.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ModalRegisterSuccessComponent } from 'src/app/pages/register/modal-register-success/modal-register-success.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { LoaderService } from 'src/app/services/loader.service';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { BypassInterceptorContext } from 'src/app/interceptors/http.interceptor';
import { ModeSwitchService } from 'src/app/services/mode-switch.service';
import { ProfileService } from 'src/app/services/profile.service';
import { EmailSignerService } from 'src/app/services/crispEmail.service';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { setRegex } from 'src/app/libs/functions/common.functions';
import { MenuAccessService } from 'src/app/services/menu-access.service';
import { Profile } from 'src/app/models/profile.model';
import { LoginResponse, LoginError } from 'src/app/models/auth.model';
import { UserLevelService } from '../../services/user-level.service';

declare var grecaptcha: any;

@Component({
  standalone: false,
  selector: 'app-auth-layout',
  templateUrl: './auth-layout.component.html',
  styleUrls: ['./auth-layout.component.scss'],
})
export class AuthLayoutComponent implements OnInit, OnDestroy {
  setRegex = setRegex;

  formGroup: FormGroup = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(50),
    ]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(32),
    ]),
  });

  isLoading: boolean = false;
  isShowPassword: boolean = false;
  errorUser: string = '';
  errorRole: string = '';
  errMessPass: string = '';
  isValidPass: boolean = true;
  validEmail: boolean = true;
  failedLogin: boolean = false;
  profileData: Profile | null = null;
  signature: string = '';

  eyeOn: string =
    'https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/eye.svg';
  eyeOff: string =
    'https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/eye-slash.svg';

  private destroy$: Subject<void> = new Subject<void>();
  private profileSubscription: Subscription = new Subscription();
  private recaptchaErrorCount: number = 0;

  constructor(
    private BaseService: BaseService,
    private renderer: Renderer2,
    private localStorageService: LocalStorageService,
    private router: Router,
    private message: NzMessageService,
    private modalService: NzModalService,
    public loader: LoaderService,
    private httpClient: HttpClient,
    private modeSwitchService: ModeSwitchService,
    private profileService: ProfileService,
    private emailSigner: EmailSignerService,
    private formValidation: FormValidationService,
    private menuAccessService: MenuAccessService,
    private userLevelService: UserLevelService
  ) {}

  /**
   * Initializes component and injects Google reCAPTCHA Enterprise script into the document head.
   */
  ngOnInit(): void {
    this.addScriptToHead(
      `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_TOKEN}`
    );
  }

  /**
   * Cleans up subscriptions and subjects to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.profileSubscription?.unsubscribe();
  }

  /**
   * Listens to Enter key globally to auto-submit the login form when form is valid and there's no user error.
   * @param event The KeyboardEvent/Event instance.
   */
  @HostListener('document:keydown.enter', ['$event'])
  handleGlobalEnter(event: Event) {
    if (!this.formGroup.invalid && this.errorUser === '') {
      event.preventDefault();
      this.getCaptchaToken(event);
    }
  }

  // ─── Validation (delegates to FormValidationService) ───

  /**
   * Validates the email form control using global validation service.
   * Updates state flags such as validEmail and errorUser.
   */
  validateEmail() {
    const value = this.formGroup.get('email')?.value || '';
    const result = this.formValidation.validateEmail(value);
    this.validEmail = result.valid;
    this.errorUser = result.message;
  }

  /**
   * Validates password format and minimum requirements.
   * Updates state flags such as errMessPass and isValidPass.
   */
  validatePassword() {
    const value = this.formGroup.get('password')?.value || '';
    const regex = /^[a-zA-Z0-9`~!()\-_+={}\[\]:;'.<>\/]+$/g;

    if (value.length === 0) {
      this.errMessPass = 'Password must be filled';
      this.isValidPass = false;
    } else if (value.length < 8) {
      this.errMessPass = 'Minimum 8 characters';
      this.isValidPass = false;
    } else if (!regex.test(value)) {
      this.isValidPass = false;
      this.errMessPass = 'There are unsupported characters';
    } else {
      this.errMessPass = '';
      this.isValidPass = true;
    }
  }

  /**
   * Enforces keypress validation pattern for password inputs.
   * @param event Keyboard event on key press.
   * @returns boolean specifying if the key is allowed.
   */
  setRegexPassword(event: KeyboardEvent) {
    return this.formValidation.validatePasswordInput(event);
  }

  /**
   * Toggles the visibility state of password input text.
   */
  showPasswordClick(): void {
    this.isShowPassword = !this.isShowPassword;
  }

  // ─── reCAPTCHA ───

  /**
   * Requests a reCAPTCHA token for REGISTER_RAJAONGKIR action and proceeds to login.
   * @param event Click/Submit event.
   */
  async getCaptchaToken(event: Event) {
    event.preventDefault();
    const token = await this.executeRecaptcha('REGISTER_RAJAONGKIR');
    if (token) {
      this.handleLogin(token, event);
    }
  }

  /**
   * Requests a reCAPTCHA token for RESEND_ACTIVATION action and posts data to resend verification email.
   * Shows success modal or displays error on failure.
   * @param event Click event.
   */
  resendVerif(event: Event) {
    event.preventDefault();

    this.executeRecaptcha('RESEND_ACTIVATION').then((token) => {
      if (!token) return;

      const params = {
        email: this.formGroup.get('email')?.value,
        recaptcha_token: token,
      };

      this.BaseService.postData(RESEND_VERIFY, params)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.modalService.create({
              nzContent: ModalRegisterSuccessComponent,
              nzFooter: null,
            });
          },
          error: () => {
            this.message.create(
              'error',
              'An error occurred. Please try again later.'
            );
          },
        });
    });
  }

  // ─── Login ───

  /**
   * Main login entry point for Sandbox environment.
   * Sets up Crisp chat signature and posts login request. Fallback to Prod is handled on error.
   * @param recaptchaToken Token obtained from reCAPTCHA execution.
   * @param event The trigger event.
   */
  handleLogin(recaptchaToken: string, event: Event) {
    if (this.formGroup.get('password')?.value.length < 6) return;

    const email = this.formGroup.get('email')?.value;
    this.signature = this.emailSigner.signEmail(email);
    this.setupCrispEmail(email);

    this.postLogin(environment.SWITCH_URL, recaptchaToken).subscribe({
      next: (res: LoginResponse) => {
        this.onLoginSuccess(res, 'sandbox');
        localStorage.setItem('mode', 'sandbox');
      },
      error: (err: LoginError) => {
        this.handleLoginError(err, recaptchaToken, event, true);
      },
    });
  }

  /**
   * Fallback login point for Production environment if Sandbox is not registered.
   * @param recaptchaToken Token obtained from reCAPTCHA execution.
   * @param event The trigger event.
   */
  private handleLoginProd(recaptchaToken: string, event: Event) {
    if (this.formGroup.get('password')?.value.length < 6) return;

    this.postLogin(environment.BASE_URL, recaptchaToken).subscribe({
      next: (res: LoginResponse) => {
        this.onLoginSuccess(res, 'production');
      },
      error: (err: LoginError) => {
        this.handleLoginError(err, recaptchaToken, event, false);
      },
    });
  }

  // ─── Private helpers ───

  /**
   * Submits an HTTP POST request to authenticate the user.
   * @param baseUrl Switch URL (sandbox) or base URL (production).
   * @param recaptchaToken Validation token.
   * @returns Observable of LoginResponse.
   */
  private postLogin(baseUrl: string, recaptchaToken: string) {
    const params = {
      email: this.formGroup.get('email')?.value,
      password: this.formGroup.get('password')?.value,
      recaptcha_token: recaptchaToken,
      fcm_token: environment.fcm_token,
    };

    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });
    const context = new HttpContext().set(BypassInterceptorContext, true);
    const apiUrl = `${baseUrl}/user/api/v2/auth/login`;

    return this.httpClient.post<LoginResponse>(apiUrl, params, { headers, context });
  }

  /**
   * Sets token/local storage properties and switches client application mode on successful login.
   * @param res Login response payload.
   * @param mode Target sandbox/production system environment mode.
   */
  private onLoginSuccess(
    res: LoginResponse,
    mode: 'sandbox' | 'production'
  ) {
    const { data } = res;
    localStorage.setItem('auth-token', data.access_token);
    localStorage.setItem('refresh-token', data.refresh_token);
    this.localStorageService.saveUser(data.name);
    
    this.modeSwitchService.switchMode(mode);
    this.getProfile(mode);
  }

  /**
   * Evaluates API login errors, initiates fallback workflows, retries on reCAPTCHA failures,
   * or sets user feedback messages.
   * @param err LoginError response payload.
   * @param recaptchaToken Active validation token.
   * @param event The trigger event.
   * @param isSandbox Specifies if Sandbox flow was active.
   */
  private handleLoginError(
    err: LoginError,
    recaptchaToken: string,
    event: Event,
    isSandbox: boolean
  ) {
    const meta = err.error?.meta;
    const errors = err.error?.data?.errors;

    // Recaptcha failure → retry
    if (meta?.code === 403 || meta?.message === 'recaptcha failed') {
      this.recaptchaErrorCount++;
      if (this.recaptchaErrorCount > 4) {
        this.message.create(
          'error',
          'Too many failed reCAPTCHA attempts. Please try again later.'
        );
      } else {
        this.getCaptchaToken(event);
      }
      return;
    }

    // Sandbox-only: fallback to production for unregistered / not activated by admin
    if (
      isSandbox &&
      (errors === 'email not registered' ||
        errors === 'user not activated by admin')
    ) {
      this.handleLoginProd(recaptchaToken, event);
      return;
    }

    // User not activated
    if (errors === 'user not activated') {
      this.errorUser = 'Your email has not been verified';
      this.validEmail = false;
      this.failedLogin = false;
      this.isValidPass = true;
      return;
    }

    // Email not registered (prod path)
    if (!isSandbox && errors === 'email not registered') {
      this.errorUser = 'Email not registered yet';
      this.validEmail = false;
      this.failedLogin = false;
      this.isValidPass = true;
      return;
    }

    // Generic error
    this.message.create(
      'error',
      'An error occurred. Please try again later.'
    );
    this.failedLogin = true;
    this.validEmail = false;
    this.isValidPass = false;
  }

  /**
   * Helper that wraps the async reCAPTCHA Enterprise execution pattern in a Promise.
   * @param action String label indicating target action.
   * @returns Promise resolving to recaptcha token string, or null on error.
   */
  private async executeRecaptcha(action: string): Promise<string | null> {
    return new Promise((resolve) => {
      grecaptcha.enterprise.ready(async () => {
        try {
          const token = await grecaptcha.enterprise.execute(
            `${RECAPTCHA_TOKEN}`,
            { action }
          );
          resolve(token);
        } catch (error) {
          console.error('Error executing reCAPTCHA:', error);
          this.message.create('error', 'Error executing reCAPTCHA');
          resolve(null);
        }
      });
    });
  }

  /**
   * Fetches user profile data, updates client layout mode, and redirects user to target dashboard/settings route.
   * @param mode Selected environment mode ('sandbox' | 'production').
   */
  private async getProfile(mode: 'sandbox' | 'production') {
    this.menuAccessService.clearCache();
    this.profileSubscription = this.profileService.profileData$
      .pipe(
        filter((data: Profile | null) => !!data),
        take(1)
      )
      .subscribe((data: Profile) => {
        this.profileData = data;
        
        if (data.rajaongkir_level === 3) {
          this.userLevelService.getUserLevel().subscribe();
        }

        this.modeSwitchService.switchMode(mode);
        this.router.navigate(['/dashboard']);
      }
    );
    // this.initLiveChat();
    this.profileService.getProfile();
  }

  /**
   * Dynamically adds a `<script>` tag to the document head with given src attribute.
   * @param src Source location URL of the script.
   */
  private addScriptToHead(src: string): void {
    const script = this.renderer.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;
    this.renderer.appendChild(document.head, script);
  }

  /**
   * Sets Crisp chat widget user email dynamic configuration parameters.
   * @param email User email string.
   */
  private setupCrispEmail(email: string): void {
    (window as any).$crisp = (window as any).$crisp || [];
    (window as any).$crisp.push(['set', 'user:email', [email, this.signature]]);
  }

  /**
   * Initializes the Crisp live chat widget with the loaded user email and Crisp settings.
   */
  private initLiveChat(): void {
    const script = this.renderer.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    (window as any).$crisp = [];
    (window as any).CRISP_WEBSITE_ID = environment.CRISP_WEBSITE_ID;
    (window as any).CRISP_TOKEN_ID = this.profileData?.email;
    (window as any).CRISP_RUNTIME_CONFIG = {
      session_merge: true,
    };
    script.type = 'text/javascript';
    script.async = true;
    this.renderer.appendChild(document.head, script);
  }
}
